import React from 'react';
import {
  Box,
  Heading,
  Icon,
  Grid,
  Button,
  HStack,
  Text,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { LuGitFork } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { selectCurrentCampaignId } from 'store/campaigns';
import { useAppSelector } from 'hooks';
import { useGetCampaignBaskets } from 'queries/baskets';
import {
  useGetCampaignProposalsOverview,
  useGetCampaignCodes,
  useUpdateProposalCodes,
} from 'queries/monitoring';
import { BasketSimple } from 'components/Basket/Basket.simple';
import { BASKET_STATUS_GROUPS_DISABLE_DEFAULT } from 'components/Basket/Basket.consts';
import { MonitoringCodesTree } from './Monitoring';
import { CampaignMonitoringCodesModal } from './Campaign.MonitoringCodes';
import { Card } from 'layouts';
import { campaignShowPage } from './Campaign.Links';
import { TableSkeleton } from 'components/Skeletons';

export const CampaignMonitoringAssignCodes = (): JSX.Element => {
  const { t } = useTranslation();
  const campaignId = useAppSelector(selectCurrentCampaignId);
  const { data: baskets } = useGetCampaignBaskets(campaignId);
  const { data: proposalsOverview } = useGetCampaignProposalsOverview(
    campaignId,
  );
  const {
    data: campaignCodes,
    isLoading: isCodesLoading,
  } = useGetCampaignCodes(campaignId);
  const isCodesPoolEmpty =
    campaignCodes?.available.length === 0 &&
    campaignCodes?.assigned.length === 0;

  const [selectedProposal, setSelectedProposal] = React.useState<number | null>(
    null,
  );

  const assignedCodeIdsByProposalId = React.useMemo(() => {
    if (!proposalsOverview) return {};
    return proposalsOverview.reduce((acc, { id, codes }) => {
      acc[id] = codes;
      return acc;
    }, {} as Record<number, Array<number>>);
  }, [proposalsOverview]);

  const selectedProposalCodeIds =
    selectedProposal != null
      ? assignedCodeIdsByProposalId[selectedProposal] ?? []
      : undefined;

  const availableCheckedCodeIdsRef = React.useRef<number[]>([]);
  const assignedCheckedCodeIdsRef = React.useRef<number[]>([]);
  const updateProposalCodes = useUpdateProposalCodes();
  // Bugfix: remount trees on every save to reset their state
  const [saveVersion, setSaveVersion] = React.useState(0);

  const handleSaveProposalCodes = () => {
    if (selectedProposal == null) return;
    updateProposalCodes.mutate(
      {
        proposalId: selectedProposal,
        codes: [
          ...availableCheckedCodeIdsRef.current,
          ...assignedCheckedCodeIdsRef.current,
        ],
      },
      { onSuccess: () => setSaveVersion(version => version + 1) },
    );
  };

  const activeBaskets = React.useMemo(
    () =>
      baskets?.filter(
        basket =>
          !BASKET_STATUS_GROUPS_DISABLE_DEFAULT.includes(basket.statusGroup),
      ) ?? [],
    [baskets],
  );

  return (
    <Box>
      <HStack justifyContent='space-between' alignItems='center' mb={3}>
        <Heading as='h2' fontSize={'xl'}>
          <Icon as={LuGitFork} />{' '}
          {t('campaign.codesAssignPage', 'Panel dopasowywania')}
        </Heading>
        <Button as={Link} to={campaignShowPage(campaignId)}>
          {t('campaign.backToCampaign', 'Powrót do kampanii')}
        </Button>
      </HStack>
      <Grid templateColumns='1fr 1fr' gap={4}>
        {!baskets && <TableSkeleton />}
        {baskets && (
          <Box>
            {activeBaskets.map(basket => (
              <BasketSimple
                key={basket.id}
                basket={basket}
                proposalsOverview={proposalsOverview ?? []}
                setSelectedProposal={setSelectedProposal}
                selectedProposal={selectedProposal}
              />
            ))}
          </Box>
        )}

        {isCodesLoading && <TableSkeleton />}
        {isCodesPoolEmpty && (
          <Box as={Card} p={5} minW={0}>
            <Text fontSize='sm' color='palette.gray.main' mb={4}>
              {t(
                'monitoring.codesPoolEmpty',
                'Pula kodów kampanii jest pusta. Dodaj kody, aby móc przypisać je do transakcji.',
              )}
            </Text>
            <CampaignMonitoringCodesModal campaignId={campaignId} />
          </Box>
        )}
        {campaignCodes && !isCodesPoolEmpty && (
          <Box as={Card} p={5} minW={0}>
            <Heading as='h3' fontSize='lg' mb={4}>
              {t('monitoring.codesManagement', 'Zarządzanie przypisaniami')}
            </Heading>
            {!selectedProposal && (
              <Text fontSize='sm' color='palette.gray.main' mb={2}>
                {t(
                  'monitoring.selectProposalHint',
                  'Wybierz transakcję z listy po lewej, aby przypisać do niej kody.\nJeśli nie ma w koszyku żadnej transakcji oznacza to, że proces wymiany materiałów nie został zakończony, a linki testowe nie zostały zaakceptowane.',
                )}
              </Text>
            )}
            <Heading as='h4' fontSize='sm' mb={1}>
              {t('monitoring.codesAssigned', 'Przypisane kody')}
            </Heading>
            <MonitoringCodesTree
              key={`assigned-${selectedProposal ?? 'none'}-${saveVersion}`}
              codesNodes={campaignCodes.assigned}
              codesType='assigned'
              campaignId={campaignId}
              mode='assign'
              selectedProposalId={selectedProposal}
              assignedProposalCodeIds={selectedProposalCodeIds}
              hideSelectProposalHint
              onCheckedCodeIdsChange={ids => {
                assignedCheckedCodeIdsRef.current = ids;
              }}
            />
            <Heading as='h4' fontSize='sm' mb={1} mt={4}>
              {t('monitoring.codesAvailable', 'Nieprzypisane kody')}
            </Heading>
            <MonitoringCodesTree
              key={`available-${selectedProposal ?? 'none'}-${saveVersion}`}
              codesNodes={campaignCodes.available}
              codesType='available'
              campaignId={campaignId}
              mode='assign'
              selectedProposalId={selectedProposal}
              assignedProposalCodeIds={selectedProposalCodeIds}
              hideSelectProposalHint
              onCheckedCodeIdsChange={ids => {
                availableCheckedCodeIdsRef.current = ids;
              }}
            />
            {/* <Tabs variant='soft-rounded' colorScheme='teal' isLazy>
              <TabList>
                <Tab>
                  {t('monitoring.codesAvailable', 'Nieprzypisane kody')}
                </Tab>
                <Tab>{t('monitoring.codesAssigned', 'Przypisane kody')}</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <MonitoringCodesTree
                    key={`${selectedProposal ?? 'none'}-${saveVersion}`}
                    codesNodes={codesAvailable}
                    codesType='available'
                    campaignId={campaignId}
                    mode='assign'
                    selectedProposalId={selectedProposal}
                    assignedProposalCodeIds={selectedProposalCodeIds}
                    onCheckedCodeIdsChange={ids => {
                      availableCheckedCodeIdsRef.current = ids;
                    }}
                  />
                </TabPanel>
                <TabPanel>
                  <MonitoringCodesTree
                    key={`${selectedProposal ?? 'none'}-${saveVersion}`}
                    codesNodes={codesAssigned}
                    codesType='assigned'
                    campaignId={campaignId}
                    mode='assign'
                    selectedProposalId={selectedProposal}
                    assignedProposalCodeIds={selectedProposalCodeIds}
                    onCheckedCodeIdsChange={ids => {
                      assignedCheckedCodeIdsRef.current = ids;
                    }}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs> */}
            {selectedProposal != null && (
              <HStack justifyContent='flex-end' mt={4}>
                <Button
                  variant='send'
                  onClick={handleSaveProposalCodes}
                  isLoading={updateProposalCodes.isLoading}
                >
                  {t(
                    'monitoring.saveSelectedCodes',
                    'Zapisz zmiany w przypisaniu kodów',
                  )}
                </Button>
              </HStack>
            )}
          </Box>
        )}
      </Grid>
    </Box>
  );
};
